import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { evalRunForCriterion, formatJudgementDateRange } from '../../hooks/useJudgementSource';
import { AiJudgementSource, AiJudgementSourceKind, EvalMarkingRun } from '../../types';

interface JudgementSourceSelectorProps {
  source: AiJudgementSource;
  evalRuns: EvalMarkingRun[];
  loadingEvalRuns: boolean;
  evalRunsError: boolean;
  usedProductionFallback?: boolean;
  selectedCriteriaId?: string;
  productionFrom?: string | null;
  productionTo?: string | null;
  onKindChange: (kind: AiJudgementSourceKind) => void;
  compact?: boolean;
}

const JudgementSourceSelector: React.FC<JudgementSourceSelectorProps> = ({
  source,
  evalRuns,
  loadingEvalRuns,
  evalRunsError,
  usedProductionFallback = false,
  selectedCriteriaId,
  productionFrom = null,
  productionTo = null,
  onKindChange,
  compact = false,
}) => {
  const matchingRun = evalRunForCriterion(evalRuns, selectedCriteriaId || null);
  const dateLabel =
    source.kind === 'eval'
      ? formatJudgementDateRange(matchingRun?.createdAt)
      : formatJudgementDateRange(productionFrom, productionTo);
  const runsDeclareCriteria = evalRuns.some(run => run.labellingCriterionIds.length > 0);
  const criterionHasNoEval =
    source.kind === 'eval' &&
    Boolean(selectedCriteriaId) &&
    !loadingEvalRuns &&
    !evalRunsError &&
    runsDeclareCriteria &&
    !matchingRun;

  return (
    <Box sx={{ mb: compact ? 0 : 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          alignItems: compact ? 'center' : 'flex-start',
          flexWrap: 'wrap',
          gap: 0.75,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            AI Judgements
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={source.kind}
            onChange={(_e, val: AiJudgementSourceKind | null) => {
              if (val !== null) onKindChange(val);
            }}
            sx={{ '& .MuiToggleButton-root': { whiteSpace: 'nowrap', px: 1.2, py: 0.25 } }}
          >
            <ToggleButton value="production">Production</ToggleButton>
            <ToggleButton value="eval">Eval Run</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {dateLabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            aria-label={`AI judgements from ${dateLabel}`}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {dateLabel}
          </Typography>
        )}
        {criterionHasNoEval && (
          <Tooltip title="No Eval Run covers this criterion. AI Judgements will stay empty rather than falling back to production.">
            <Box
              component="span"
              aria-label="No Eval Run for this criterion"
              tabIndex={0}
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <WarningAmberIcon color="warning" fontSize="small" />
            </Box>
          </Tooltip>
        )}
      </Box>
      {source.kind === 'eval' && evalRunsError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load Eval Runs. Switch to Production or try again later.
        </Alert>
      )}
      {source.kind === 'eval' && !loadingEvalRuns && !evalRunsError && evalRuns.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No Eval Runs were found. Switch to Production to view production judgements.
        </Alert>
      )}
    </Box>
  );
};

export default JudgementSourceSelector;
